import { useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { storeMedia, getMedia } from '../services/storage';
import { settingsStorage } from '../services/settingsStorage';
import { TFunction } from 'react-i18next';
// v5: bouton Standby personnalisable
import StandbyButton from '../components/StandbyButton';
import {
  getStandbyButtonDesign,
  DEFAULT_STANDBY_BUTTON,
  type StandbyButtonConfig,
} from '../services/standbyButtonDesign';
import React from 'react';

interface CleanupSettings {
  timeoutSeconds: number;
  mediaType: 'image' | 'video' | 'none';
  slideshowInterval: number;
  isStandbyEnabled: boolean;
}

interface MediaItem {
  url: string;
  fileName?: string;
  id?: string;
  sizeMB?: number;
  type?: 'image' | 'video';
}

const CLEANUP_MEDIA_KEY = 'diaporamaImages';
const LAST_ACTIVITY_KEY = 'lastActivityTime';

export const useAutoCleanup = (t: TFunction<"translation", undefined>) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const isCleanupActiveRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isExitingStandbyRef = useRef(false);
  // v5: Root React pour rendre le bouton Standby personnalisable
  const standbyBtnRootRef = useRef<Root | null>(null);
  const standbyBtnConfigRef = useRef<StandbyButtonConfig>(DEFAULT_STANDBY_BUTTON);
  const [settings, setSettings] = useState<CleanupSettings>({
    timeoutSeconds: 60,
    mediaType: 'none',
    slideshowInterval: 5000,
    isStandbyEnabled: false, // désactivé par défaut
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const isTransitioningRef = useRef(false);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const storedSettings = await settingsStorage.getStandbySettings();
        setSettings({
          timeoutSeconds: storedSettings.timeoutSeconds,
          mediaType: 'none',
          slideshowInterval: storedSettings.slideshowInterval,
          isStandbyEnabled: storedSettings.isStandbyEnabled,
        });
        console.log('[CLEANUP] Paramètres chargés depuis IndexedDB:', storedSettings);
      } catch (error) {
        console.error('[CLEANUP] Erreur chargement paramètres:', error);
      } finally {
        setIsInitialized(true);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const loadCleanupMedia = async () => {
      try {
        const media = await getMedia(CLEANUP_MEDIA_KEY);
        let mediaArray: (string | MediaItem)[] = [];
        if (media) {
          try {
            mediaArray = JSON.parse(media);
            if (!Array.isArray(mediaArray)) mediaArray = [media];
          } catch {
            mediaArray = [];
          }
        }

        const isValidUrl = (item: string | MediaItem) => {
          const url = typeof item === 'string' ? item : item.url;
          return (
            typeof url === 'string' &&
            url.trim() !== '' &&
            (url.startsWith('data:image/') || url.startsWith('data:video/mp4'))
          );
        };

        const validMedia = mediaArray.filter(isValidUrl);
        const mediaType =
          validMedia.length > 0
            ? validMedia.some(item => {
                const url = typeof item === 'string' ? item : item.url;
                return url.startsWith('data:video');
              })
              ? 'video'
              : 'image'
            : 'none';

        console.log('[CLEANUP] Médias bruts:', mediaArray.map(item => ({
          type: typeof item === 'string' ? (item.startsWith('data:video') ? 'video' : 'image') : item.type || 'unknown',
          length: typeof item === 'string' ? item.length : item.url?.length,
          fileName: typeof item === 'object' ? item.fileName : 'N/A',
          id: typeof item === 'object' ? item.id : 'N/A',
          preview: typeof item === 'string' ? item.slice(0, 50) + '...' : item.url?.slice(0, 50) + '...',
        })));
        console.log('[CLEANUP] Médias validés:', validMedia.map(item => ({
          type: typeof item === 'string' ? (item.startsWith('data:video') ? 'video' : 'image') : item.type || 'unknown',
          length: typeof item === 'string' ? item.length : item.url?.length,
          fileName: typeof item === 'object' ? item.fileName : 'N/A',
          id: typeof item === 'object' ? item.id : 'N/A',
          preview: typeof item === 'string' ? item.slice(0, 50) + '...' : item.url?.slice(0, 50) + '...',
        })), 'mediaType:', mediaType);

        setSettings(prev => ({
          ...prev,
          mediaType,
        }));
      } catch (error) {
        console.error('[CLEANUP] Erreur chargement médias:', error);
        setSettings(prev => ({ ...prev, mediaType: 'none' }));
      }
    };
    loadCleanupMedia();
  }, []);

  // v5 perf : `localStorage.setItem` est SYNCHRONE et bloquait le défilement tactile
  // (une écriture à chaque mousemove/scroll/touch). On écrit au plus 1 fois par seconde :
  // la veille compte en secondes, l'écart maximal est donc d'1 s.
  const lastActivityWriteRef = useRef(0);
  const updateLastActivity = (force = false) => {
    const now = Date.now();
    if (!force && now - lastActivityWriteRef.current < 1000) return;
    lastActivityWriteRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  };

  const checkInactivity = async () => {
    if (!settings.isStandbyEnabled) {
      console.log('[CLEANUP] Écran de veille désactivé:', { isStandbyEnabled: settings.isStandbyEnabled });
      return;
    }
    const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || Date.now().toString());
    const inactiveTime = Date.now() - lastActivity;
    const timeoutMs = settings.timeoutSeconds * 1000;

    console.log('[CLEANUP] Vérification inactivité:', {
      inactiveTime,
      timeoutMs,
      isCleanupActive: isCleanupActiveRef.current,
    });

    if (inactiveTime >= timeoutMs && !isCleanupActiveRef.current) {
      console.log('[CLEANUP] Inactivité détectée, lancement nettoyage');
      await performCleanup();
    }
  };

  const isValidBase64Video = (src: string): boolean => {
    if (!src.startsWith('data:video/mp4;base64,')) return false;
    const base64Part = src.split(',')[1];
    if (!base64Part) return false;
    return base64Part.length > 100 && /^[A-Za-z0-9+/=]+$/.test(base64Part);
  };

  const loadVideo = (videoElement: HTMLVideoElement, src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      videoElement.src = '';
      videoElement.onloadeddata = () => {
        console.log('[CLEANUP] Vidéo chargée:', src.slice(0, 50) + '...');
        resolve();
      };
      videoElement.onerror = (e) => {
        const error = (e.target as HTMLVideoElement).error;
        if (error?.code === 4 && videoElement.readyState >= 2) {
          console.log('[CLEANUP] Erreur transitoire ignorée, vidéo jouée');
          resolve();
          return;
        }
        console.error('[CLEANUP] Erreur chargement vidéo:', {
          src: src.slice(0, 50) + '...',
          errorCode: error?.code,
          errorMessage: error?.message,
        });
        reject(e);
      };
      videoElement.onloadedmetadata = () => {
        if (videoElement.duration > 30) {
          console.error('[CLEANUP] Vidéo trop longue:', videoElement.duration);
          reject(new Error('Vidéo trop longue (max 30s)'));
        }
      };
      videoElement.src = src;
      videoElement.load();
    });
  };

  const preloadImage = (imageUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        console.log('[CLEANUP] Image préchargée:', imageUrl.slice(0, 50) + '...');
        resolve();
      };
      img.onerror = () => {
        console.error('[CLEANUP] Erreur chargement image:', imageUrl.slice(0, 50) + '...');
        reject(new Error('Erreur chargement image'));
      };
    });
  };

  const performCleanup = async () => {
    if (isCleanupActiveRef.current) {
      console.log('[CLEANUP] Nettoyage déjà actif, ignoré');
      return;
    }

    try {
      isCleanupActiveRef.current = true;
      const mediaList = await getMedia(CLEANUP_MEDIA_KEY);
      let validMedia: (string | MediaItem)[] = [];
      if (mediaList) {
        validMedia = typeof mediaList === 'string' ? JSON.parse(mediaList) : mediaList;
        validMedia = validMedia.filter(
          item => {
            const url = typeof item === 'string' ? item : item.url;
            return (
              typeof url === 'string' &&
              url.trim() !== '' &&
              (url.startsWith('data:image/') || url.startsWith('data:video/mp4'))
            );
          }
        );
      }

      console.log('[CLEANUP] Début nettoyage...', {
        validMedia: validMedia.map(item => ({
          type: typeof item === 'string' ? (item.startsWith('data:video') ? 'video' : 'image') : item.type || 'unknown',
          length: typeof item === 'string' ? item.length : item.url?.length,
          fileName: typeof item === 'object' ? item.fileName : 'N/A',
          id: typeof item === 'object' ? item.id : 'N/A',
          preview: typeof item === 'string' ? item.slice(0, 50) + '...' : item.url?.slice(0, 50) + '...',
        })),
        mediaType: settings.mediaType,
      });

      const standbyScreen = document.createElement('div');
      standbyScreen.id = 'standby-screen';
      standbyScreen.className = 'fixed inset-0 z-50 bg-black';

      standbyScreen.innerHTML = `
        <div class="relative w-full h-full overflow-hidden bg-black">
          <div id="media-container" class="absolute inset-1 rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.5)] backdrop-blur-sm">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10"></div>
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]"></div>
            <img 
              id="standby-image" 
              src="${validMedia.length > 0 && !(typeof validMedia[currentMediaIndex] === 'string' ? validMedia[currentMediaIndex] : validMedia[currentMediaIndex].url).startsWith('data:video') ? (typeof validMedia[currentMediaIndex] === 'string' ? validMedia[currentMediaIndex] : validMedia[currentMediaIndex].url) : ''}" 
              class="w-full h-full object-cover scale-105 transform transition-transform duration-[2s] hover:scale-110" 
              alt="Veille"
              style="display: ${validMedia.length > 0 && !(typeof validMedia[currentMediaIndex] === 'string' ? validMedia[currentMediaIndex] : validMedia[currentMediaIndex].url).startsWith('data:video') ? 'block' : 'none'}"
            />
            <video 
              id="standby-video"
              class="w-full h-full object-cover scale-105 transform transition-transform duration-[2s]" 
              autoplay 
              muted 
              playsinline
              ${validMedia.length === 1 && (typeof validMedia[0] === 'string' ? validMedia[0] : validMedia[0].url).startsWith('data:video') ? 'loop' : ''}
              src="${validMedia.length > 0 && (typeof validMedia[currentMediaIndex] === 'string' ? validMedia[currentMediaIndex] : validMedia[currentMediaIndex].url).startsWith('data:video') ? (typeof validMedia[currentMediaIndex] === 'string' ? validMedia[currentMediaIndex] : validMedia[currentMediaIndex].url) : ''}"
              style="display: none"
            ></video>
            <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/20"></div>
            <div class="absolute inset-0 border border-white/5 rounded-[2rem]"></div>
          </div>
          
          <!-- v5: container de montage pour le bouton React personnalisable -->
          <div id="standby-button-mount" class="absolute inset-0 pointer-events-none">
            <!-- Le bouton React sera monté ici, avec son propre positionnement absolu -->
            <!-- pointer-events-none sur le wrapper, mais le bouton interne aura pointer-events: auto via son style -->
          </div>
        </div>
      `;

      // v5 perf : feuille de style insérée UNE seule fois (avant : une de plus à chaque
      // mise en veille, jamais retirée → des dizaines par jour, recalcul des styles).
      const existingStyle = document.getElementById('standby-keyframes-style');
      const style = (existingStyle as HTMLStyleElement) || document.createElement('style');
      style.id = 'standby-keyframes-style';
      style.textContent = `
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes spin-reverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes spin-slower { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes glow { 0%, 100% { opacity: 0.5; } 50% { opacity: 0.8; } }
        @keyframes glow-reverse { 0%, 100% { opacity: 0.8; } 50% { opacity: 0.5; } }
        @keyframes orb-1 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(100px, 50px); }
          50% { transform: translate(50px, 100px); }
          75% { transform: translate(-50px, 50px); }
        }
        @keyframes orb-2 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-100px, -50px); }
          50% { transform: translate(-50px, -100px); }
          75% { transform: translate(50px, -50px); }
        }
        @keyframes orb-3 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(50px, -100px); }
          50% { transform: translate(100px, -50px); }
          75% { transform: translate(-50px, -100px); }
        }
        .animate-spin-slow { animation: spin-slow 15s linear infinite; }
        .animate-spin-reverse { animation: spin-reverse 12s linear infinite; }
        .animate-spin-slower { animation: spin-slower 18s linear infinite; }
        .animate-glow { animation: glow 3s ease-in-out infinite; }
        .animate-glow-reverse { animation: glow-reverse 3s ease-in-out infinite; }
        .animate-orb-1 { animation: orb-1 15s ease-in-out infinite; }
        .animate-orb-2 { animation: orb-2 12s ease-in-out infinite; }
        .animate-orb-3 { animation: orb-3 18s ease-in-out infinite; }
      `;
      if (!existingStyle) document.head.appendChild(style);
      document.body.appendChild(standbyScreen);

      const standbyImage = document.getElementById('standby-image') as HTMLImageElement;
      const standbyVideo = document.getElementById('standby-video') as HTMLVideoElement;

      if (validMedia.length > 0) {
        const currentMedia = validMedia[currentMediaIndex];
        const currentUrl = typeof currentMedia === 'string' ? currentMedia : currentMedia.url;

        if (currentUrl.startsWith('data:video') && standbyVideo) {
          if (!isValidBase64Video(currentUrl)) {
            console.warn('[CLEANUP] Base64 vidéo initiale invalide:', currentUrl.slice(0, 50) + '...');
            setCurrentMediaIndex(prev => (prev + 1) % validMedia.length);
            isTransitioningRef.current = false;
            scheduleNextMedia(validMedia);
          } else {
            loadVideo(standbyVideo, currentUrl)
              .then(() => {
                if (standbyImage) standbyImage.style.display = 'none';
                standbyVideo.style.display = 'block';
                standbyVideo.play().catch(error => {
                  console.error('[CLEANUP] Erreur démarrage vidéo initiale:', error);
                  setCurrentMediaIndex(prev => (prev + 1) % validMedia.length);
                  scheduleNextMedia(validMedia);
                });
                if (validMedia.length > 1) {
                  standbyVideo.onended = () => {
                    console.log('[CLEANUP] Vidéo initiale terminée, passage au suivant');
                    setCurrentMediaIndex(prev => {
                      const nextIndex = (prev + 1) % validMedia.length;
                      isTransitioningRef.current = false;
                      scheduleNextMedia(validMedia);
                      return nextIndex;
                    });
                  };
                }
                isTransitioningRef.current = false;
              })
              .catch(() => {
                setCurrentMediaIndex(prev => (prev + 1) % validMedia.length);
                isTransitioningRef.current = false;
                scheduleNextMedia(validMedia);
              });
          }
        } else if (currentUrl.startsWith('data:image') && standbyImage) {
          preloadImage(currentUrl)
            .then(() => {
              standbyImage.src = currentUrl;
              standbyImage.style.display = 'block';
              if (validMedia.length > 1) {
                transitionTimeoutRef.current = setTimeout(() => {
                  isTransitioningRef.current = false;
                  scheduleNextMedia(validMedia);
                }, settings.slideshowInterval);
              }
              isTransitioningRef.current = false;
            })
            .catch(error => {
              console.error('[CLEANUP] Échec affichage image initiale:', error);
              setCurrentMediaIndex(prev => (prev + 1) % validMedia.length);
              isTransitioningRef.current = false;
              scheduleNextMedia(validMedia);
            });
        } else {
          isTransitioningRef.current = false;
        }
      } else {
        isTransitioningRef.current = false;
      }

      const scheduleNextMedia = async (validMedia: (string | MediaItem)[]) => {
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
          transitionTimeoutRef.current = null;
        }

        if (isTransitioningRef.current) {
          console.log('[CLEANUP] Transition en cours, attente...');
          return;
        }

        transitionTimeoutRef.current = setTimeout(async () => {
          isTransitioningRef.current = true;

          setCurrentMediaIndex(prev => {
            const nextIndex = (prev + 1) % validMedia.length;
            const nextMedia = validMedia[nextIndex];
            const nextUrl = typeof nextMedia === 'string' ? nextMedia : nextMedia.url;
            console.log('[CLEANUP] Média suivant:', {
              index: nextIndex,
              type: nextUrl.startsWith('data:video') ? 'video' : 'image',
              fileName: typeof nextMedia === 'object' ? nextMedia.fileName : 'N/A',
              id: typeof nextMedia === 'object' ? nextMedia.id : 'N/A',
              preview: nextUrl.slice(0, 50) + '...',
            });

            if (nextUrl.startsWith('data:video')) {
              if (standbyVideo) {
                if (!isValidBase64Video(nextUrl)) {
                  console.warn('[CLEANUP] Base64 vidéo invalide:', nextUrl.slice(0, 50) + '...');
                  isTransitioningRef.current = false;
                  scheduleNextMedia(validMedia);
                  return nextIndex;
                }
                loadVideo(standbyVideo, nextUrl)
                  .then(() => {
                    if (standbyImage) standbyImage.style.display = 'none';
                    standbyVideo.style.display = 'block';
                    standbyVideo.play().catch(error => {
                      console.error('[CLEANUP] Erreur lecture vidéo:', error);
                      setCurrentMediaIndex(next => (next + 1) % validMedia.length);
                      scheduleNextMedia(validMedia);
                    });
                    standbyVideo.onended = () => {
                      console.log('[CLEANUP] Vidéo terminée, passage au suivant');
                      setCurrentMediaIndex(next => {
                        const nextNextIndex = (next + 1) % validMedia.length;
                        isTransitioningRef.current = false;
                        scheduleNextMedia(validMedia);
                        return nextNextIndex;
                      });
                    };
                    isTransitioningRef.current = false;
                  })
                  .catch(() => {
                    isTransitioningRef.current = false;
                    scheduleNextMedia(validMedia);
                  });
              }
            } else {
              if (standbyVideo) {
                standbyVideo.pause();
                standbyVideo.style.display = 'none';
              }
              if (standbyImage) {
                preloadImage(nextUrl)
                  .then(() => {
                    standbyImage.src = nextUrl;
                    standbyImage.style.display = 'block';
                    transitionTimeoutRef.current = setTimeout(() => {
                      isTransitioningRef.current = false;
                      scheduleNextMedia(validMedia);
                    }, settings.slideshowInterval);
                  })
                  .catch(error => {
                    console.error('[CLEANUP] Échec affichage image:', error);
                    setCurrentMediaIndex(next => (next + 1) % validMedia.length);
                    isTransitioningRef.current = false;
                    scheduleNextMedia(validMedia);
                  });
              }
            }
            return nextIndex;
          });
        }, 100);
      };

      // v5: monter le composant React StandbyButton dans le container
      // BLOC TRY/CATCH GLOBAL: si quoi que ce soit plante, on n'empêche pas
      // l'écran de veille de s'afficher
      try {
        const mountPoint = document.getElementById('standby-button-mount');
        if (mountPoint) {
          // Nettoyer l'ancienne root si existe
          if (standbyBtnRootRef.current) {
            try { standbyBtnRootRef.current.unmount(); } catch (_) {}
            standbyBtnRootRef.current = null;
          }
          const root = createRoot(mountPoint);
          standbyBtnRootRef.current = root;

          // Charger la config la plus récente
          getStandbyButtonDesign()
            .then((cfg) => {
              standbyBtnConfigRef.current = cfg;
              try {
                root.render(
                  React.createElement(StandbyButton, {
                    config: cfg,
                    fallbackText: t('Cliquez pour commander'),
                    onClick: () => {
                      isExitingStandbyRef.current = true;
                      restoreFromCleanup();
                      setTimeout(() => {
                        isExitingStandbyRef.current = false;
                      }, 500);
                    },
                  })
                );
              } catch (renderErr) {
                console.error('[Standby] render error', renderErr);
              }
            })
            .catch((e) => console.error('[Standby] config load error', e));
        }
      } catch (mountErr) {
        console.error('[Standby] mount error', mountErr);
      }

      standbyScreen.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        // Le clic sur le bouton (ou son contenu) doit passer par le bouton React,
        // pas par cet écouteur global
        const btn = target.closest('.standby-btn-custom');
        if (!btn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          isExitingStandbyRef.current = true;
          restoreFromCleanup();
          setTimeout(() => {
            isExitingStandbyRef.current = false;
          }, 500);
        }
      }, { capture: true });

      standbyScreen.addEventListener('remove', () => {
        console.log('[CLEANUP] Arrêt diaporama');
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
          transitionTimeoutRef.current = null;
        }
      }, { once: true });

      console.log('[CLEANUP] Nettoyage terminé');
    } catch (error) {
      console.error('[CLEANUP] Erreur pendant nettoyage:', error);
      isCleanupActiveRef.current = false;
    }
  };

  const restoreFromCleanup = async () => {
    if (!isCleanupActiveRef.current) return;

    try {
      // v5: démonter le bouton React avant de retirer le screen
      if (standbyBtnRootRef.current) {
        try {
          standbyBtnRootRef.current.unmount();
        } catch (_) {}
        standbyBtnRootRef.current = null;
      }

      const standbyScreen = document.getElementById('standby-screen');
      if (standbyScreen) {
        const standbyVideo = document.getElementById('standby-video') as HTMLVideoElement;
        if (standbyVideo) {
          standbyVideo.pause();
          if (standbyVideo.src) {
            URL.revokeObjectURL(standbyVideo.src);
          }
          standbyVideo.src = '';
          standbyVideo.load();
        }
        const standbyImage = document.getElementById('standby-image') as HTMLImageElement;
        if (standbyImage) {
          standbyImage.src = '';
        }
        standbyScreen.remove();
      }

      if (videoRef.current) {
        videoRef.current.pause();
        if (videoRef.current.src) {
          URL.revokeObjectURL(videoRef.current.src);
        }
        videoRef.current.src = '';
        videoRef.current.load();
        videoRef.current = null;
      }

      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }

      setCurrentMediaIndex(0);
      isCleanupActiveRef.current = false;
      console.log('[CLEANUP] Écran veille désactivé');
    } catch (error) {
      console.error('[CLEANUP] Erreur restauration:', error);
    }
  };

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isCleanupActiveRef.current) {
      restoreFromCleanup();
    }

    updateLastActivity(true);

    timeoutRef.current = setTimeout(() => {
      checkInactivity();
    }, settings.timeoutSeconds * 1000);
  };

  const saveSettings = async (newSettings: Partial<CleanupSettings>) => {
    try {
      const settingsToStore: CleanupSettings = {
        timeoutSeconds: newSettings.timeoutSeconds ?? settings.timeoutSeconds,
        mediaType: newSettings.mediaType ?? settings.mediaType,
        slideshowInterval: newSettings.slideshowInterval ?? settings.slideshowInterval,
        isStandbyEnabled: newSettings.isStandbyEnabled ?? settings.isStandbyEnabled,
      };

      console.log('[CLEANUP] Sauvegarde paramètres:', settingsToStore);

      await settingsStorage.saveStandbySettings({
        timeoutSeconds: settingsToStore.timeoutSeconds,
        slideshowInterval: settingsToStore.slideshowInterval,
        isStandbyEnabled: settingsToStore.isStandbyEnabled,
      });

      setSettings(settingsToStore);
      setCurrentMediaIndex(0);
      if (isCleanupActiveRef.current) {
        await restoreFromCleanup();
        if (settingsToStore.isStandbyEnabled) {
          await performCleanup();
        }
      }

      console.log('[CLEANUP] Paramètres enregistrés dans IndexedDB:', settingsToStore);
    } catch (error) {
      console.error('[CLEANUP] Erreur sauvegarde:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!isInitialized) return;

    const events = ['mousedown', 'click', 'keydown', 'touchstart', 'scroll', 'mousemove'];

    const handleActivity = (e: Event) => {
      if (isExitingStandbyRef.current && (e.type === 'click' || e.type === 'mousedown' || e.type === 'touchstart')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('[CLEANUP] Clic bloqué pendant sortie mode veille');
        return;
      }

      if (isCleanupActiveRef.current) {
        console.log('[CLEANUP] Activité détectée, restauration...');
        resetTimeout();
      } else {
        updateLastActivity();
      }
    };

    updateLastActivity(true);

    const checkInterval = setInterval(() => {
      checkInactivity();
    }, 1000);

    events.forEach(event => {
      if (event === 'click' || event === 'mousedown' || event === 'touchstart') {
        window.addEventListener(event, handleActivity, { capture: true, passive: false });
      } else {
        window.addEventListener(event, handleActivity, { passive: true });
      }
    });

    resetTimeout();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });

      clearInterval(checkInterval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      restoreFromCleanup();
    };
  }, [isInitialized, settings.timeoutSeconds, settings.mediaType, settings.slideshowInterval, settings.isStandbyEnabled]);

  return {
    settings,
    saveSettings,
  };
};